import { DotsVerticalIcon, DownloadIcon } from '@heroicons/react/outline';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/solid';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useGetPrescriptions } from 'hooks/useEscripts';
import useTablePaginator from 'hooks/useTablePaginator';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { Action, Fab } from 'react-tiny-fab';
import { API_URL } from 'utils/config';
import { handleDownloadLink } from 'utils/helpers';
import { IEscriptPrescriptionData, IEscriptPrescriptionSummary, IEscriptSupplementSummary } from 'utils/models';

// components
import PageWrap from 'components/PageWrap';
import Table from 'components/Table';
import Tooltip from 'components/Tooltip';

const ESCRIPT_TOKEN_STATUS_CX = {
  PENDING: {
    label: 'Pending',
    className: 'bg-yellow-500'
  },
  ACTIVE: {
    label: 'Active',
    className: 'bg-[#C0CBB7]'
  },
  INVOICED: {
    label: 'Invoiced',
    className: 'bg-[#8D3E30] text-white'
  },
  PAID: {
    label: 'Paid',
    className: 'bg-[#CE744B] text-white'
  },
  READY_FOR_DISPENSING: {
    label: 'Ready for Dispensing',
    className: 'bg-[#0F5342] text-white'
  },
  OVERDUE: {
    label: 'Overdue',
    className: 'bg-red-500'
  },
  DISPENSING: {
    label: 'Dispensing',
    className: 'bg-[#0F5342] text-white'
  },
  DISPENSED: {
    label: 'Dispensed',
    className: 'bg-[#0F5342] text-white'
  },
  SHIPPING: {
    label: 'Shipping',
    className: 'bg-green-500'
  },
  DELIVERED: {
    label: 'Delivered',
    className: 'bg-green-500'
  },
  INACTIVE: {
    label: 'Inactive',
    className: 'bg-green-500'
  },
  VOIDED: {
    label: 'Void',
    className: 'bg-white'
  },
  EXPIRED: {
    label: 'Expired',
    className: 'bg-red-500'
  },
  EXPORTED: {
    label: 'Exported',
    className: 'bg-green-500'
  },
  TRANSFERRED: {
    label: 'Transferred',
    className: 'bg-green-500'
  },
  ON_HOLD: {
    label: 'On hold',
    className: 'bg-green-500'
  }
};

const Prescriptions: FC = () => {
  const [filterTokenStatus, setFilterTokenStatus] = useState<string>('');

  const { setPage, page, search, setSearch, perPage, setPerPage } = useTablePaginator();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { data, isFetching } = useGetPrescriptions({
    page,
    perPage,
    tokenStatus: filterTokenStatus,
    search,
    date: {
      from: startDate,
      to: endDate
    }
  });

  const columns: Column<IEscriptPrescriptionData>[] = useMemo(
    () => [
      {
        id: 'expander',
        Header: 'Patient Name',
        accessor: 'patient_name',
        Cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <div {...row.getToggleRowExpandedProps()} className="cursor-pointer">
              {row.isExpanded ? (
                <ChevronDownIcon className="h-6 w-6 text-black" />
              ) : (
                <ChevronRightIcon className="h-6 w-6 text-black" />
              )}
            </div>
            <div className="space-y-1">
              <div className="font-bold text-hazel-solid-green-600">
                {row.original.patient_name}
              </div>
              <div>DOB: {row.original.date_of_birth}</div>
            </div>
          </div>
        )
      },
      {
        Header: 'Prescription',
        accessor: 'active_products',
        Cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <div className="font-bold text-2xl text-hazel-light-green-600">
              {row.original.active_products}
            </div>
            <div className="font-bold">
              <div>Active</div>
              <div>Products</div>
            </div>
            <div className="font-bold text-2xl text-hazel-light-green-600">
              {row.original.active_supplements.length}
            </div>
            <div className="font-bold">
              <div>Active</div>
              <div>Supplements</div>
            </div>
          </div>
        )
      },
      {
        Header: 'Tokens',
        accessor: 'active_tokens',
        minWidth: 300,
        // Filter: ({ column }) => (
        //   <Select
        //     options={[
        //       {
        //         label: 'Show All',
        //         value: ''
        //       },
        //       ...ESCRIPT_TOKEN_STATUS.map((item: string) => ({ label: item, value: item }))
        //     ]}
        //     value={filterTokenStatus}
        //     onChange={(status: string) => {
        //       setFilterTokenStatus(status);
        //     }}
        //     className="my-2"
        //   />
        // ),
        Cell: ({ row }) => (
          <div className="flex items-center space-x-2">
            <div className="font-bold text-2xl text-hazel-light-green-600">
              {row.original.active_tokens}
            </div>
            <div className="font-bold">
              <div>Active</div>
              <div>Tokens</div>
            </div>
          </div>
        )
      }
    ],
    [filterTokenStatus]
  );

  const onChangeDate = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const [start, end] = date;

      setStartDate(start);
      setEndDate(end);
    } else {
      setStartDate(date);
    }

    // reset
    setPage(1);
  };

  const renderRowSubComponent = useCallback(({ row }) => {
    const summary = row.original.summary as IEscriptPrescriptionSummary[];
    const supplements = row.original.active_supplements as IEscriptSupplementSummary[];
    return (
      <>
        {summary.map((item, index) => (
          <Fragment key={index}>
            <div />
            <div className="py-2 px-4 md:py-8 md:px-6 border-gray-300 text-sm flex items-center bg-gray-25 md:mb-5 md:ml-5 rounded-l-lg">
              <div className="space-y-1">
                <div className="font-bold">{item.product.brand}</div>
                <div>
                  {item.product.name} {item.product.quantity}
                </div>
              </div>
            </div>
            <div className="py-2 px-4 md:py-8 md:px-6 border-gray-300 text-sm flex items-center bg-gray-25 md:mb-5 md:mr-5 rounded-r-lg">
              <div className="space-y-2">
                {item.product.tokens.map((token, indexToken) => (
                  <Tooltip
                    text={
                      <div className="space-y-0.25">
                        <div className="font-bold">
                          {ESCRIPT_TOKEN_STATUS_CX[token.status].label} TOKEN - {token.doctor}
                        </div>
                        <div>
                          Consultation #: {token.consultation_no} - {token.consultation_date}
                        </div>
                        <div>
                          Expiry:{' '}
                          {token.expiry ? format(new Date(token.expiry), 'dd/MM/yyyy') : '-'}
                        </div>
                      </div>
                    }
                    position="bottom"
                    key={indexToken}
                    className={ESCRIPT_TOKEN_STATUS_CX[token.status].className}
                  >
                    <div
                      className={clsx(
                        'w-4 h-4 rounded-full border border-gray-400',
                        ESCRIPT_TOKEN_STATUS_CX[token.status].className
                      )}
                    />
                  </Tooltip>
                ))}
              </div>
            </div>
          </Fragment>
        ))}
        {supplements.map((item, index) => (
          <Fragment key={index}>
            <div />
            <div className="py-2 px-4 md:py-8 md:px-6 border-gray-300 text-sm flex items-center bg-gray-25 md:mb-5 md:ml-5 rounded-l-lg">
              <div className="space-y-1">
                <div className="font-bold">{item.supplement.name}</div>
              </div>
            </div>
            <div className="py-2 px-4 md:py-8 md:px-6 border-gray-300 text-sm flex items-center bg-gray-25 md:mb-5 md:mr-5 rounded-r-lg">
              <div className="space-y-2">
                
              </div>
            </div>
          </Fragment>
        ))}
      </>
    );
  }, []);

  return (
    <PageWrap title="Prescription List">
      <div className="p-5 rounded-md">
        <div className="grid gap-20 cols-grid-12">
          <div className="col-span-12">
            <Table
              totalCount={data.data.length > 0 ? data.count : 0}
              columns={columns}
              data={data?.data || []}
              theme="version-1"
              page={page}
              onSetPage={setPage}
              search={search}
              onSearch={setSearch}
              perPage={perPage}
              onPerPageSelect={setPerPage}
              isLoading={isFetching}
              renderRowSubComponent={renderRowSubComponent}
              actions={
                <Fab icon={<DotsVerticalIcon className="w-6 h-6" />} alwaysShowTitle>
                  <Action
                    text="Export"
                    onClick={() => handleDownloadLink(`${API_URL}/external/scripts/export`)}
                  >
                    <DownloadIcon className="h-5 w-5" />
                  </Action>
                </Fab>
              }
              isDateRange
              onChangeDate={onChangeDate}
            />
          </div>
        </div>
      </div>
    </PageWrap>
  );
};

export default Prescriptions;
